export const careerStories = [
  {
    slug: 'remindermedia',
    index: '01',
    company: 'ReminderMedia',
    role: 'Vice President of Sales',
    period: 'November 2023—Present',
    shortPeriod: '2023—Present',
    image: '/assets/katherine-working-1.webp',
    imageAlt: 'Katherine William-Botrous working at a conference table',
    eyebrow: 'Current chapter / Sales leadership',
    summary: 'Growth-focused Vice President of Sales at ReminderMedia with full P&L ownership and extreme ownership across sales, marketing, design, product development, and training — building and leading an elite 22-person account executive organization.',
    intro: 'As Vice President of Sales at ReminderMedia, Katherine holds full P&L ownership and cross-functional accountability across sales, marketing, design, product development, and training — an extreme-ownership mandate she has translated into measurable revenue growth, a transformed sales organization, and stronger customer outcomes.',
    focus: [
      'Sales leadership',
      'P&L ownership',
      'Revenue growth',
      'Customer success',
      'High-performance teams',
    ],
    chapters: [
      {
        label: 'The role',
        title: 'Full P&L ownership and cross-functional leadership',
        body: 'Katherine joined ReminderMedia as Vice President of Sales in November 2023 with full P&L and budget ownership and cross-functional accountability spanning sales, marketing, design, product development, and training. Her mandate covers the entire business unit — from lead generation and sales strategy through product development and operational efficiency.',
      },
      {
        label: 'The team',
        title: 'Building an elite sales organization',
        body: 'Katherine scaled the sales organization from 5 to 22 elite, full-cycle account executives who prospect, close, and manage their own books of business. She also redesigned the commission structure to a percentage-based model, improving payout transparency and incentivizing full-cycle account ownership.',
      },
      {
        label: 'The customer',
        title: 'Stronger outcomes across the business',
        body: 'Katherine redesigned marketing and lead generation strategy to improve efficiency and effectiveness, elevated service delivery and customer experience standards across the organization, and directed product development of new platform features that increased customer retention and added lead-generation and performance-tracking capabilities for clients. She also eliminated a three-day design production backlog through automation, achieving same-day turnaround with the queue cleared nightly.',
      },
    ],
    outcomes: [
      { value: 'Full P&L', label: 'ownership of the business unit' },
      { value: 'Cross-functional', label: 'accountability across six departments' },
      { value: '5 → 22', label: 'elite full-cycle account executives' },
      { value: 'Same-day', label: 'design turnaround after clearing a 3-day backlog' },
    ],
    sourceNote: 'Results reflect Katherine’s verified career record across sales, operations, and client success.',
    seoTitle: 'ReminderMedia Sales Leadership | Katherine William-Botrous',
    seoDescription: 'Katherine William-Botrous is Vice President of Sales at ReminderMedia, a sales, client success, and operations leader with full P&L ownership and extreme ownership across sales, marketing, design, and product development — building elite teams and driving revenue, retention, and customer outcomes.',
  },
  {
    slug: 'telvista',
    index: '02',
    company: 'Telvista',
    role: 'Senior Executive Director, Operations',
    period: 'August 2019—November 2023',
    shortPeriod: '2019—2023',
    image: '/assets/katherine-working-2.webp',
    imageAlt: 'Katherine William-Botrous in a professional working environment',
    eyebrow: 'Career chapter / Operations leadership',
    summary: 'At Telvista, Katherine progressed through talent acquisition and support-services leadership into the role of Senior Executive Director, Operations.',
    intro: 'The verified record shows a four-year progression across recruiting, training, quality, leadership development, and operations—an executive path built through increasingly broad organizational responsibility.',
    focus: [
      'Operations leadership',
      'Talent acquisition',
      'Training and quality',
      'Leadership development',
    ],
    chapters: [
      {
        label: 'Progression',
        title: 'Three roles, expanding responsibility',
        body: 'Katherine joined Telvista as Director of Talent Acquisition in August 2019, became Director of Support Services in February 2020, and advanced to Senior Executive Director, Operations in January 2021.',
      },
      {
        label: 'Organizational scope',
        title: 'Building the systems behind performance',
        body: 'As Director of Support Services, she oversaw recruiting, training, quality, and leadership development for the corporate headquarters. Those functions form the infrastructure through which teams are hired, prepared, coached, and held to consistent standards.',
      },
      {
        label: 'Leadership signal',
        title: 'Growth through cross-functional leadership',
        body: 'Her progression demonstrates an ability to move from a specialized talent mandate into broader support-services and operations leadership. The public résumé does not assign numerical outcomes to this employer, so this page does not borrow metrics from another role.',
      },
    ],
    outcomes: [
      { value: '3', label: 'successive leadership roles' },
      { value: '4+ years', label: 'verified Telvista tenure' },
      { value: '4', label: 'support functions overseen' },
    ],
    sourceNote: 'Results reflect Katherine’s verified career record across sales, operations, and client success.',
    seoTitle: 'Telvista Operations Leadership | Katherine William-Botrous',
    seoDescription: 'Explore Katherine William-Botrous’s Telvista leadership across operations, customer service, and tech support — owning call-center operations for Verizon in the U.S. and Mexico with more than 1,500 headcount.',
  },
  {
    slug: 'ipacesetters',
    index: '03',
    company: 'iPacesetters',
    role: 'Client Success & Operations Leadership',
    period: '2008—2019',
    shortPeriod: '2008—2019',
    image: '/assets/katherine-working-3.webp',
    imageAlt: 'Katherine William-Botrous reviewing work on a tablet',
    eyebrow: 'Career chapter / Measurable transformation',
    summary: 'Across quality, operations, site leadership, and client success at iPacesetters, Katherine led large teams and translated operational discipline into measurable gains in retention, quality, cost, growth, and profitability.',
    intro: 'This is the most fully documented chapter in Katherine’s public résumé. The results below are tied directly to the roles and initiatives described in that source.',
    focus: [
      'Site leadership',
      'Client success',
      'Sales operations',
      'Retention and quality',
    ],
    clientLogos: [
      'AT&T', 'Wells Fargo', 'Finger Hut', 'Bank of America',
      'Citi Bank', 'GE', 'Power House', 'UHC',
      'Spectrum', 'ADT', 'American Water', 'SiriusXM',
      'Metro by T-Mobile', 'Fanatics', 'Verizon', 'Government accounts',
    ],
    chapters: [
      {
        label: 'Scale',
        title: 'Leading a major operating site',
        body: 'As Corporate Headquarters Site Director, Katherine held responsibility for a Tulsa call center of approximately 300–375 employees, including financial performance, retention, day-to-day operations, and teams spanning frontline sales, data entry, posted sales, customer service management, customer satisfaction management, quality assurance, recruiting, administration, operations, and human resources.',
      },
      {
        label: 'Operating system',
        title: 'Changing structures, policies, and leadership practice',
        body: 'She developed a three-level manager training program and implemented more than 20 process or policy changes. She also reorganized the call-center structure, introduced retention and morale programs, and created pre-orientation and red-flag meetings to address training and operational turnover. She built a sourcing tracker to maximize recruiting return on investment and launched Facebook marketing and advertising for Tulsa that drove more than 36% of all hires.',
      },
      {
        label: 'Growth',
        title: 'Scaling accounts and strategic relationships',
        body: 'Earlier in her operations leadership, Katherine grew a Spectrum account from 17 to 189 full-time employees and launched a Spanish line of business within three weeks. Her AT&T work included strategic account relationships, solution selling techniques to grow client base and revenue, sales operations growth, forecasting, training, and executive reviews.',
      },
    ],
    outcomes: [
      { value: '38% → 17%', label: 'turnover within six months' },
      { value: '96–98%', label: 'monthly quality performance' },
      { value: '42%', label: 'indirect labor cost reduction' },
      { value: '300–375', label: 'employees at the Tulsa site' },
      { value: '20+', label: 'process or policy improvements' },
      { value: '#1', label: 'site/vendor ranking across accounts' },
      { value: '45%+', label: 'company volume and revenue handled by Tulsa facility' },
      { value: '12%', label: 'profitability impact from operating restructure' },
      { value: '60%', label: 'training-turnover reduction' },
      { value: '36%', label: 'of all hires via Facebook marketing' },
      { value: '5 → 81', label: 'Spanish line of business in three weeks' },
    ],
    sourceNote: 'Results reflect Katherine’s verified career record across sales, operations, and client success.',
    seoTitle: 'iPacesetters Operations Impact | Katherine William-Botrous',
    seoDescription: 'See verified iPacesetters outcomes from Katherine William-Botrous — a sales, client success, and operations leader covering retention, quality, cost, team scale, enterprise client relationships, and operational improvement.',
  },
];

export const careerStoryBySlug = Object.fromEntries(careerStories.map((story) => [story.slug, story]));
